import React, { useState, useEffect, useMemo } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import rootAxios from 'requests/rootAxios';
import { TrainingRequests } from 'requests/TrainingRequests';
import trainingTypes from 'Constants/trainingTypes';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { fetchDailyEmployeesWhoJoined } from 'thunks/trainingThunks/thunk';
import NewTableComponent from 'Component/NewTable';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import LoopIcon from '@mui/icons-material/Loop';

// Extend dayjs with custom parse format
dayjs.extend(customParseFormat);

// Status icon function (same as AllTrainings)
const getStatusIcon = (row) => {
  const currentDate = dayjs();
  const parsedStartDate = dayjs(row.startDate, 'DD/MM/YYYY');
  const parsedEndDate = dayjs(row.endDate, 'DD/MM/YYYY');
  const isApproved = row.isApproved ?? false;
  const isStartingSoon = parsedStartDate.isAfter(currentDate);
  const isInProgress =
    !isStartingSoon &&
    (parsedStartDate.isSame(currentDate) || parsedStartDate.isBefore(currentDate)) &&
    (parsedEndDate.isSame(currentDate) || parsedEndDate.isAfter(currentDate));
  const isFinished = parsedEndDate.isBefore(currentDate);

  if (!isApproved) {
    return (
      <div>
        <WorkHistoryIcon color="error" />
        {`Approval Pending ${row.pendingAt || ''}`}
      </div>
    );
  }
  if (isStartingSoon) {
    return (
      <div>
        <AccessAlarmIcon color="error" />
        Starting Soon
      </div>
    );
  }

  if (isInProgress) {
    const Icon = row.attendencePending || row.feedbackPending ? WarningAmberIcon : LoopIcon;
    const color = row.attendencePending || row.feedbackPending ? 'warning' : 'action';
    const text = row.attendencePending
      ? 'Attendance pending'
      : row.feedbackPending
        ? 'Feedback pending'
        : 'In Progress';

    return (
      <div>
        <Icon color={color} />
        {text}
      </div>
    );
  }

  if (isFinished) {
    const Icon = row.attendencePending || row.feedbackPending ? WarningAmberIcon : CheckCircleIcon;
    const color = row.attendencePending || row.feedbackPending ? 'warning' : 'success';
    const text = row.attendencePending
      ? 'Attendance pending'
      : row.feedbackPending
        ? 'Feedback pending'
        : 'Completed';

    return (
      <div>
        <Icon color={color} />
        {text}
      </div>
    );
  }

  return null;
};

const ClusterView = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [statusFilterTab, setStatusFilterTab] = useState(0); // Separate state for status filter tabs
  const [pendencyData, setPendencyData] = useState({});
  const [trainingsData, setTrainingsData] = useState([]);
  const { loadingPendecy: pendencyLoading } = useSelector((state) => state.trainingSlice);

  // const [pendencyLoading, setPendencyLoading] = useState(false);
  const [trainingsLoading, setTrainingsLoading] = useState(false);

  // Filter states
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedTrainingType, setSelectedTrainingType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // AbortController for cancelling pending API calls
  const [abortController, setAbortController] = useState(null);

  // Local loading state for regions and branches
  const [regionsBranchesLoading, setRegionsBranchesLoading] = useState(false);

  // Store all available regions and branches for dropdown options
  const [allAvailableRegions, setAllAvailableRegions] = useState([]);
  // Cluster-mapped regions for this user — used as the default selection / clear-filters target
  const [defaultRegions, setDefaultRegions] = useState([]);
  const [allAvailableBranches, setAllAvailableBranches] = useState([]);
  const [allAvailableTrainers, setAllAvailableTrainers] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [trainersLoading, setTrainersLoading] = useState(false);
  // Training types for tabs
  const trainingTypeTabs = useMemo(
    () => [
      { key: 'REGULAR_INDUCTION', label: 'Induction', value: trainingTypes.REGULAR_INDUCTION },
      { key: 'COLLECTION_TRAINING', label: 'Collection', value: trainingTypes.COLLECTION_TRAINING },
      // {
      //   key: 'INDIVIDUAL_LENDING',
      //   label: 'Individual Lending',
      //   value: trainingTypes.INDIVIDUAL_LENDING,
      // },
      { key: 'PRAGATI_TRAINING', label: 'Pragati', value: trainingTypes.PRAGATI_TRAINING },
      {
        key: 'PRODUCT_FINANCING',
        label: 'PRODUCT FINANCING',
        value: trainingTypes.PRODUCT_FINANCING,
      },
    ],
    []
  );

  console.log('selectedTrainers', selectedTrainers);
  // Function to categorize batches by status (same as AllTrainings)
  const categorizeBatches = (batches) => {
    const currentDate = dayjs();

    return batches.reduce(
      (acc, batch) => {
        const parsedStartDate = dayjs(batch.startDate, 'DD/MM/YYYY');
        const parsedEndDate = dayjs(batch.endDate, 'DD/MM/YYYY');
        const isApproved = batch.isApproved ?? false;
        const isStartingSoon = parsedStartDate.isAfter(currentDate);
        const isInProgress =
          !isStartingSoon &&
          (parsedStartDate.isSame(currentDate) || parsedStartDate.isBefore(currentDate)) &&
          (parsedEndDate.isSame(currentDate) || parsedEndDate.isAfter(currentDate));
        const isFinished = parsedEndDate.isBefore(currentDate);

        // Check for attendance or feedback pending
        const hasActionPending = batch.attendencePending || batch.feedbackPending;

        if (!isApproved) {
          acc.approvalPending.push(batch);
        } else if (isStartingSoon) {
          acc.startingSoon.push(batch);
        } else if (isInProgress) {
          if (hasActionPending) {
            acc.actionPending.push(batch);
          } else {
            acc.ongoing.push(batch);
          }
        } else if (isFinished) {
          if (hasActionPending) {
            acc.actionPending.push(batch);
          } else {
            acc.completed.push(batch);
          }
        }

        return acc;
      },
      {
        completed: [],
        ongoing: [],
        startingSoon: [],
        approvalPending: [],
        actionPending: [],
      }
    );
  };

  // Function to get data for active tab (same as AllTrainings)
  const getDataForActiveTab = () => {
    const categorized = categorizeBatches(trainingsData);
    const tabData = {
      0: { data: trainingsData, label: 'All' },
      1: { data: categorized.completed, label: 'Completed' },
      2: { data: categorized.ongoing, label: 'Ongoing' },
      3: { data: categorized.startingSoon, label: 'Upcoming' },
      4: { data: categorized.approvalPending, label: 'Approval Pending' },
      5: { data: categorized.actionPending, label: 'Action Pending' },
    };

    return tabData[statusFilterTab] || { data: [], label: 'All' };
  };

  // Date formatting function
  const formatDateOfJoining = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date
        .toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
        .replace(/\//g, '-');
    } catch (error) {
      return dateString;
    }
  };

  // Function to get trainers mapped to a region
  const getTrainersForRegion = (regionName) => {
    if (!regionName || !allAvailableRegions.length || !allAvailableTrainers.length) {
      return [];
    }

    // Find the region ID for the given region name
    const region = allAvailableRegions.find((r) => r.regionName === regionName);
    if (!region) {
      return [];
    }

    // Find trainers whose regionIdsMapped includes this region's ID
    const trainers = allAvailableTrainers.filter((trainer) =>
      trainer.regionIdsMapped?.includes(region.regionId)
    );

    return trainers.map((trainer) => trainer.employeeName || `${trainer.employeeId}`).join(', ');
  };

  const getSortedEmployees = (trainingTypeKey) => {
    let employees = pendencyData[trainingTypeKey] || [];

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      employees = employees.filter((employee) => {
        const trainers = getTrainersForRegion(employee.regionName);
        return (
          employee.employeeId?.toLowerCase().includes(searchLower) ||
          employee.employeeName?.toLowerCase().includes(searchLower) ||
          employee.funcTitleName?.toLowerCase().includes(searchLower) ||
          employee.regionName?.toLowerCase().includes(searchLower) ||
          employee.branchName?.toLowerCase().includes(searchLower) ||
          trainers?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Default sorting by date of joining in descending order
    return [...employees].sort((a, b) => {
      const aDate = new Date(a.dateOfJoining || 0);
      const bDate = new Date(b.dateOfJoining || 0);
      return bDate - aDate;
    });
  };

  // Fetch regions and branches on component mount and auto-fill
  useEffect(() => {
    const initializeRegionsAndBranches = async () => {
      try {
        setRegionsBranchesLoading(true);

        // Fetch the user's cluster-mapped regions to use as the default selection
        const regionsResponse = await rootAxios.get('/user/getRegion');
        if (regionsResponse?.data?.data?.regions) {
          const regions = regionsResponse.data.data.regions;
          console.log('Setting regions:', regions);
          // As per the requirement, auto select all the regions
          setSelectedRegions(regions); // Default-select the cluster-mapped regions
          setAllAvailableRegions(regions); // Store all regions for dropdown

          // Fetch branches for all regions
          const regionIds = regions.map((region) => region.regionId);
          console.log('Fetching branches for region IDs:', regionIds);
          const branchesResponse = await rootAxios.post('/user/getRegionBatches', {
            regions: regionIds,
          });

          if (branchesResponse?.data?.data) {
            const branches = branchesResponse.data.data;
            console.log('Setting branches:', branches);
            setSelectedBranches(branches); // Auto-select all branches
            setAllAvailableBranches(branches); // Store all branches for dropdown

            if (branches.length === 0) {
              toast.warning(
                'No branches found for the selected regions. Please contact administrator.'
              );
            }
          } else {
            toast.warning(
              'No branches found for the selected regions. Please contact administrator.'
            );
          }
        } else {
          toast.warning('No regions found. Please contact administrator.');
        }
      } catch (error) {
        console.error('Error initializing regions and branches:', error);
        toast.error('Failed to initialize regions and branches');
      } finally {
        setRegionsBranchesLoading(false);
      }
    };

    initializeRegionsAndBranches();
  }, []);

  // Fetch trainers on component mount
  useEffect(() => {
    const fetchTrainers = async () => {
      try {
        setTrainersLoading(true);
        const trainers = await TrainingRequests.getAllClusterTrainers();
        if (trainers && trainers.length > 0) {
          console.log('Setting trainers:', trainers);
          setAllAvailableTrainers(trainers);
        } else {
          console.warn('No trainers found in response');
          setAllAvailableTrainers([]);
        }
      } catch (error) {
        console.error('Error fetching trainers:', error);
        toast.error('Failed to fetch trainers');
        setAllAvailableTrainers([]);
      } finally {
        setTrainersLoading(false);
      }
    };

    fetchTrainers();
  }, []);

  // Function to update branches when regions change
  const updateBranchesForRegions = async (selectedRegions) => {
    try {
      setRegionsBranchesLoading(true);

      if (selectedRegions.length === 0) {
        setSelectedBranches([]);
        return;
      }

      // Fetch branches for selected regions
      const regionIds = selectedRegions.map((region) => region.regionId);
      const branchesResponse = await rootAxios.post('/user/getRegionBatches', {
        regions: regionIds,
      });

      if (branchesResponse?.data?.data) {
        const branches = branchesResponse.data.data;
        setSelectedBranches(branches);
        setAllAvailableBranches(branches);

        if (branches.length === 0) {
          toast.warning('No branches found for the selected regions.');
        }
      } else {
        setSelectedBranches([]);
        toast.warning('No branches found for the selected regions.');
      }
    } catch (error) {
      console.error('Error updating branches for regions:', error);
      toast.error('Failed to update branches for selected regions');
      setSelectedBranches([]);
    } finally {
      setRegionsBranchesLoading(false);
    }
  };

  // Fetch pendency data when regions, branches, or training type changes
  useEffect(() => {
    if (selectedRegions.length > 0 && selectedBranches.length > 0) {
      fetchPendencyData(selectedRegions, selectedBranches);
      fetchTrainingsData();
    }
  }, [selectedRegions, selectedBranches, selectedTrainingType]);

  // Fetch trainings data
  useEffect(() => {
    fetchTrainingsData();
  }, []);

  // Auto-select first training type for pendency
  useEffect(() => {
    if (trainingTypeTabs.length > 0 && !selectedTrainingType) {
      setSelectedTrainingType(trainingTypeTabs[0]);
    }
  }, [trainingTypeTabs, selectedTrainingType]);

  const fetchPendencyData = async (regions, branches) => {
    let newAbortController = null;
    try {
      // Abort any previous request
      if (abortController) {
        console.log('fetchPendencyData: Aborting previous request');
        abortController.abort();
      }

      // Create new AbortController for this request
      newAbortController = new AbortController();
      setAbortController(newAbortController);

      // setPendencyLoading(true);
      setPendencyData({});
      const pendencyDataTemp = {};

      // If training type is selected, only fetch for that type
      const tabsToFetch = selectedTrainingType
        ? trainingTypeTabs.filter((tab) => tab.key === selectedTrainingType.key)
        : trainingTypeTabs;

      for (const tab of tabsToFetch) {
        // Check if request was aborted
        if (newAbortController.signal.aborted) {
          console.log('Request was aborted, stopping pendency data fetch');
          return;
        }

        const payload = {
          trainingType: tab.value,
          region: regions.map((region) => ({ id: region.regionId, label: region.regionName })),
          branches: branches.map((branch) => ({ id: branch.BRANCH_ID, label: branch.BRANCH_NAME })),
        };

        try {
          const response = await dispatch(fetchDailyEmployeesWhoJoined(payload)).unwrap();

          if (response?.employees) {
            pendencyDataTemp[tab.key] = response.employees;
          } else {
            pendencyDataTemp[tab.key] = [];
          }
        } catch (tabError) {
          console.log('tabError', tabError);
          if (tabError?.name === 'CanceledError') {
            console.log(`Request for ${tab.label} was aborted`);
            return;
          }
          console.error(`Error fetching pendency data for ${tab.label}:`, tabError);
          pendencyDataTemp[tab.key] = [];
          //toast.error(`Failed to fetch ${tab.label} pendency data`);
        }
      }

      // Only update state if request wasn't aborted
      if (!newAbortController.signal.aborted) {
        setPendencyData(pendencyDataTemp);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Pendency data fetch was aborted');
        return;
      }
      console.error('Error fetching pendency data:', error);
      toast.error('Failed to fetch pendency data');
    } finally {
      // setPendencyLoading(false);
    }
  };

  const fetchTrainingsData = async () => {
    try {
      setTrainingsLoading(true);
      // Fetch all trainings and filter by cluster (this will be implemented based on backend logic)
      const response = await rootAxios.get('/user/getAllBatches');
      if (response?.data?.data?.listOfBatches) {
        // Filter trainings based on cluster (to be implemented)
        // Ensure all required fields are present for status icon
        const trainingsWithStatus = response.data.data.listOfBatches.map((batch) => ({
          ...batch,
          // Ensure these fields exist for status calculation
          isApproved: batch.isApproved ?? false,
          attendencePending: batch.attendencePending ?? false,
          feedbackPending: batch.feedbackPending ?? false,
          pendingAt: batch.createdAt || batch.startDate,
        }));
        let trainerFilter = selectedTrainers.map((elem) => {
          return elem.employeeId;
        });
        let filteredTrainings = trainingsWithStatus;
        console.log('filteredTrainings', filteredTrainings, trainerFilter);
        if (trainerFilter.length > 0) {
          filteredTrainings = trainingsWithStatus.filter((ba) => trainerFilter.includes(ba.empid));
          setTrainingsData(filteredTrainings);
        } else {
          setTrainingsData(trainingsWithStatus);
        }
      }
    } catch (error) {
      console.error('Error fetching trainings data:', error);
      toast.error('Failed to fetch trainings data');
    } finally {
      setTrainingsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset status filter tab when switching between main tabs
    setStatusFilterTab(0);
  };

  const handleStatusFilterTabChange = (event, newValue) => {
    setStatusFilterTab(newValue);
  };

  const handleRefreshPendencyData = async () => {
    if (selectedRegions.length > 0 && selectedBranches.length > 0) {
      await fetchPendencyData(selectedRegions, selectedBranches);
    }
  };

  const downloadPendencyReport = async (trainingTypeKey, format = 'excel') => {
    const employees = pendencyData[trainingTypeKey] || [];
    if (employees.length === 0) {
      toast.warning('No data to download');
      return;
    }

    try {
      const downloadData = employees.map((employee) => ({
        'Employee ID': employee.employeeId,
        Name: employee.employeeName,
        'Function Title': employee.funcTitleName,
        Region: employee.regionName,
        Trainers: getTrainersForRegion(employee.regionName) || '-',
        Branch: employee.branchName,
        'Date of Joining': formatDateOfJoining(employee.dateOfJoining),
        'Nomination Count': employee.nominationCount,
      }));

      if (format === 'excel') {
        const XLSX = await import('xlsx');
        const ws = XLSX.utils.json_to_sheet(downloadData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Pendency Report');
        XLSX.writeFile(wb, `${trainingTypeKey}_pendency_report.xlsx`);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  // Pendency table columns (same as AllTrainings)
  const pendencyColumns = [
    {
      label: 'Employee ID',
      field: 'employeeId',
      sortable: true,
      width: '120px',
    },
    {
      label: 'Employee Name',
      field: 'employeeName',
      sortable: true,
      width: '200px',
    },
    {
      label: 'Functional Title',
      field: 'funcTitleName',
      sortable: true,
      width: '200px',
    },
    {
      label: 'Date of Joining',
      field: 'dateOfJoining',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Region',
      field: 'regionName',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Trainers',
      field: 'trainers',
      sortable: true,
      width: '200px',
      render: (row) => {
        const trainers = getTrainersForRegion(row.regionName);
        return trainers || '-';
      },
      sortValue: (row) => {
        const trainers = getTrainersForRegion(row.regionName);
        return trainers || '';
      },
    },
    {
      label: 'Branch',
      field: 'branchName',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Nomination Count',
      field: 'nominationCount',
      sortable: true,
      width: '150px',
      render: (row) => row.nominationCount || 0,
    },
  ];

  const batchesColumns = [
    {
      label: 'Batch ID',
      field: 'batchId',
      sortable: true,
      width: '120px',
    },
    {
      label: 'Start Date',
      field: 'startDate',
      sortable: true,
      width: '120px',
      sortValue: (row) => {
        // Convert DD/MM/YYYY string to dayjs object for proper sorting
        return dayjs(row.startDate, 'DD/MM/YYYY').toDate();
      },
    },
    {
      label: 'End Date',
      field: 'endDate',
      sortable: true,
      width: '120px',
      sortValue: (row) => {
        // Convert DD/MM/YYYY string to dayjs object for proper sorting
        return dayjs(row.endDate, 'DD/MM/YYYY').toDate();
      },
    },
    {
      label: 'No. of Trainees',
      field: 'listOfTrainess',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Status',
      field: 'status',
      sortable: true,
      width: '200px',
      render: (row) => getStatusIcon(row),
      sortValue: (row) => {
        const status = getStatusIcon(row)?.props.children[1];
        return status || '';
      },
    },
  ];

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Cluster View
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                options={allAvailableRegions || []}
                getOptionLabel={(option) => option.regionName}
                value={selectedRegions}
                onChange={(event, newValue) => {
                  setSelectedRegions(newValue);
                  // Update branches when regions change
                  if (newValue.length > 0) {
                    updateBranchesForRegions(newValue);
                  } else {
                    setSelectedBranches([]);
                  }
                }}
                loading={regionsBranchesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regions"
                    placeholder="Choose regions"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {regionsBranchesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.regionId}
                      label={option.regionName}
                      size="small"
                    />
                  ))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '65px !important',
                    maxHeight: '100px !important',
                    overflow: 'scroll !important',
                  },
                }}
                helperText="Select regions to view pendency data"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                options={allAvailableBranches || []}
                getOptionLabel={(option) => option.BRANCH_NAME}
                value={selectedBranches}
                onChange={(event, newValue) => setSelectedBranches(newValue)}
                loading={regionsBranchesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branches"
                    placeholder="Branches auto-updated"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {regionsBranchesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.BRANCH_ID}
                      label={option.BRANCH_NAME}
                      size="small"
                    />
                  ))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '65px !important',
                    maxHeight: '100px !important',
                    overflow: 'scroll !important',
                  },
                }}
                helperText="Branches automatically updated based on selected regions"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                options={allAvailableTrainers || []}
                getOptionLabel={(option) => `${option.employeeName} (${option.employeeId})`}
                value={selectedTrainers}
                onChange={(event, newValue) => {
                  setSelectedTrainers(newValue);
                  // Filter regions based on selected trainers
                  if (newValue.length > 0) {
                    // Extract all region IDs from all selected trainers and flatten the array
                    const trainerRegionIds = [
                      ...new Set(newValue.flatMap((trainer) => trainer.regionIdsMapped || [])),
                    ];
                    const filteredRegions = allAvailableRegions.filter((region) =>
                      trainerRegionIds.includes(region.regionId)
                    );
                    setSelectedRegions(filteredRegions);
                    // Update branches for filtered regions
                    if (filteredRegions.length > 0) {
                      updateBranchesForRegions(filteredRegions);
                    } else {
                      setSelectedBranches([]);
                    }
                  } else {
                    // If no trainers selected, reset to the default cluster-mapped regions
                    setSelectedRegions(allAvailableRegions);
                    updateBranchesForRegions(allAvailableRegions);
                  }
                }}
                loading={trainersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="List of Trainers"
                    placeholder="Select trainers"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {trainersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.employeeId}
                      label={`${option.employeeName} (${option.employeeId})`}
                      size="small"
                    />
                  ))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingRight: '65px !important',
                    maxHeight: '100px !important',
                    overflow: 'scroll !important',
                  },
                }}
                helperText="Select trainers to filter regions and branches"
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  {selectedRegions?.length === 0 && (
                    <Typography variant="body2" color="error">
                      No regions available. Please contact your administrator.
                    </Typography>
                  )}
                  {selectedBranches?.length === 0 && (
                    <Typography variant="body2" color="warning">
                      No branches available for the regions.
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedTrainingType(null);
                    setSearchTerm('');
                    setSelectedTrainers([]);
                    setSelectedRegions(allAvailableRegions);
                    updateBranchesForRegions(allAvailableRegions);
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="cluster view tabs">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Pendency
                  {Object.values(pendencyData).reduce(
                    (total, employees) => total + employees.length,
                    0
                  ) > 0 && (
                    <Chip
                      label={Object.values(pendencyData).reduce(
                        (total, employees) => total + employees.length,
                        0
                      )}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Batches
                  {trainingsData.length > 0 && (
                    <Chip label={trainingsData.length} size="small" color="success" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6">Pendency Data</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshPendencyData}
                  disabled={pendencyLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadPendencyReport(
                      (selectedTrainingType || trainingTypeTabs[0]).key,
                      'excel'
                    )
                  }
                >
                  Download Report
                </Button>
              </Box>
            </Box>

            {/* Filter Summary */}
            {(selectedTrainingType || searchTerm) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Active Filters:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {selectedTrainingType && (
                    <Chip
                      label={`Type: ${selectedTrainingType.label}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {searchTerm && (
                    <Chip
                      label={`Search: "${searchTerm}"`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Results Summary */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Pending:{' '}
                    {Object.values(pendencyData).reduce(
                      (total, employees) => total + employees.length,
                      0
                    )}
                  </Typography>
                  {searchTerm && (
                    <Typography variant="body2" color="textSecondary">
                      Filtered Results:{' '}
                      {Object.values(pendencyData).reduce((total, employees) => {
                        const filtered = employees.filter((employee) => {
                          const searchLower = searchTerm.toLowerCase();
                          const trainers = getTrainersForRegion(employee.regionName);
                          return (
                            employee.employeeId?.toLowerCase().includes(searchLower) ||
                            employee.employeeName?.toLowerCase().includes(searchLower) ||
                            employee.funcTitleName?.toLowerCase().includes(searchLower) ||
                            employee.regionName?.toLowerCase().includes(searchLower) ||
                            employee.branchName?.toLowerCase().includes(searchLower) ||
                            trainers?.toLowerCase().includes(searchLower)
                          );
                        });
                        return total + filtered.length;
                      }, 0)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {pendencyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {/* Pendency Tabs for Induction and Collection */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs
                    value={
                      selectedTrainingType
                        ? trainingTypeTabs.findIndex((tab) => tab.key === selectedTrainingType.key)
                        : 0
                    }
                    onChange={(event, newValue) =>
                      setSelectedTrainingType(trainingTypeTabs[newValue])
                    }
                    aria-label="pendency training type tabs"
                  >
                    {trainingTypeTabs.map((tab) => (
                      <Tab
                        key={tab.key}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {tab.label}
                            {pendencyData[tab.key]?.length > 0 && (
                              <Chip
                                label={pendencyData[tab.key].length}
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </Tabs>
                </Box>

                {/* Pendency Table */}
                {selectedTrainingType ? (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        {selectedTrainingType.label} (
                        {getSortedEmployees(selectedTrainingType.key).length} of{' '}
                        {pendencyData[selectedTrainingType.key]?.length || 0})
                      </Typography>
                      {searchTerm &&
                        getSortedEmployees(selectedTrainingType.key).length !==
                          (pendencyData[selectedTrainingType.key]?.length || 0) && (
                          <Chip
                            label={`Filtered from ${pendencyData[selectedTrainingType.key]?.length || 0}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                    </Box>

                    <NewTableComponent
                      tableData={getSortedEmployees(selectedTrainingType.key)}
                      columns={pendencyColumns}
                      tableName={`${selectedTrainingType.label} Pendency`}
                      count={getSortedEmployees(selectedTrainingType.key).length}
                      loading={pendencyLoading}
                      enableSearch={true}
                      enableFilters={false}
                      searchPlaceholder="Search employees..."
                      useWholeRowClicked={false}
                      emptyMessage="No pending employees found for this training type"
                      maxHeight="600px"
                      getRowStyle={(row) => {
                        // Apply red/white highlight logic similar to AllTrainings
                        const nominationCount = row.nominationCount || 0;
                        let daysDiff = 0;
                        try {
                          if (row.dateOfJoining && row.dateOfJoining.includes('-')) {
                            const [day, month, year] = row.dateOfJoining.split('-').map(Number);
                            if (day && month && year) {
                              const dateOfJoining = new Date(year, month - 1, day);
                              const currentDate = new Date();
                              daysDiff = Math.floor(
                                (currentDate - dateOfJoining) / (1000 * 60 * 60 * 24)
                              );
                            }
                          }
                        } catch (error) {
                          console.warn('Error parsing date:', row.dateOfJoining, error);
                        }

                        if (nominationCount > 1 || daysDiff >= 15) {
                          return { backgroundColor: '#ef9a9a' }; // Red background
                        }
                        return { backgroundColor: '#f5f5f5' }; // White background
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      Please select a training type to view pendency data
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {/* Status Tabs (same as AllTrainings) */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={statusFilterTab}
                onChange={handleStatusFilterTabChange}
                aria-label="training status tabs"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    minHeight: '48px',
                    px: 3,
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">All</Typography>
                      <Chip
                        label={trainingsData.length}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography variant="body2">Completed</Typography>
                      <Chip
                        label={categorizeBatches(trainingsData).completed.length}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LoopIcon sx={{ fontSize: 20, color: 'info.main' }} />
                      <Typography variant="body2">Ongoing</Typography>
                      <Chip
                        label={categorizeBatches(trainingsData).ongoing.length}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessAlarmIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                      <Typography variant="body2">Upcoming</Typography>
                      <Chip
                        label={categorizeBatches(trainingsData).startingSoon.length}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkHistoryIcon sx={{ fontSize: 20, color: 'error.main' }} />
                      <Typography variant="body2">Approval Pending</Typography>
                      <Chip
                        label={categorizeBatches(trainingsData).approvalPending.length}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                      <Typography variant="body2">Action Pending</Typography>
                      <Chip
                        label={categorizeBatches(trainingsData).actionPending.length}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Batches Table */}
            <Box className="mt-2">
              <NewTableComponent
                tableData={getDataForActiveTab().data}
                columns={batchesColumns}
                tableName={`${getDataForActiveTab().label} Trainings`}
                count={getDataForActiveTab().data.length}
                loading={trainingsLoading}
                enableSearch={true}
                enableFilters={false}
                searchPlaceholder={`Search ${getDataForActiveTab().label.toLowerCase()} batches...`}
                useWholeRowClicked={false}
                emptyMessage={`No ${getDataForActiveTab().label.toLowerCase()} batches found`}
                maxHeight="600px"
              />
            </Box>
          </Box>
        )}

        {/* No Data Message */}
        {Object.values(pendencyData).every((employees) => employees.length === 0) &&
          trainingsData.length === 0 &&
          !pendencyLoading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                No Data Available
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                {Object.values(pendencyData).every((employees) => employees.length === 0)
                  ? 'No pending employees found for the selected regions and branches.'
                  : 'No data found for the selected filters.'}
              </Typography>
            </Box>
          )}
      </Box>
    </Layout>
  );
};

export default ClusterView;
